import { promiseTimeout, unrefElement, useElementBounding, useWindowSize } from '@vueuse/core';
import { getWalkthroughTarget } from '@/composables/useWalkthroughTarget';
import { runWalkthroughSetup, runWalkthroughTeardown } from '@/composables/walkthroughSetups';

/**
 * Walkthrough Controller: a single, app-wide observer — module-level state,
 * mirroring the target registry's singleton pattern (useWalkthroughTarget.js),
 * not a fresh instance per caller. Every caller shares the same running
 * walkthrough: one component (mounted once) renders the actual Tutorial
 * instance against `tutorialStyle`; everywhere else (a modal card's CTA, a
 * What's New panel item) just calls start()/advance()/cancel() without
 * rendering anything itself.
 *
 * `settleActiveIndex` is the single place that both advances to a step and
 * skips it if its target isn't currently *visible* (see `isStepTargetVisible`
 * — mounted is not enough) — it does not auto-navigate or wait for a target to
 * appear, it just moves forward until it finds a visible target or runs out of
 * steps, which ends the walkthrough. A watcher at the bottom of this file
 * routes "the active step's target just vanished" through the same function.
 * `cancel()` is a distinct, user-initiated exit path, added separately so it
 * can't be conflated with this skip behavior.
 *
 * A step may declare its own `route`/`setup` (see ADR 0006 in
 * docs/contexts/feature-announcements/adr/) — that's how a walkthrough spans
 * more than one page or panel state: each step is responsible for getting the
 * UI into the shape it personally needs, rather than one shared `setup`
 * covering every step. `stepNeedsPrep`/`prepareStep` below are what make a
 * step transition asynchronous only when a step actually declares something
 * to do; a step with neither behaves exactly as before (purely synchronous).
 */
const steps = ref([]);
const activeIndex = ref(-1);

const isRunning = computed(() => activeIndex.value >= 0);
const totalSteps = computed(() => steps.value.length);
const activeStep = computed(() => steps.value[activeIndex.value] ?? null);

/** True while a step transition is navigating/running a setup/waiting to settle. */
const isTransitioning = ref(false);

const activeTargetElement = computed(() =>
  activeStep.value ? getWalkthroughTarget(activeStep.value.stepId).value : null,
);

const { top, left, right, bottom, width, height, update } = useElementBounding(activeTargetElement);
const { width: viewportWidth, height: viewportHeight } = useWindowSize();

/**
 * The rendered tooltip, handed over by whichever component renders it (Walkthrough/
 * Controller.vue). Its size is needed to keep it on screen: without measuring it, the only
 * option is to centre it on the target and hope, which clips whenever a target sits near a
 * viewport edge.
 */
const tutorialElement = ref(null);

const {
  width: tutorialWidth,
  height: tutorialHeight,
  update: updateTutorialBounding,
} = useElementBounding(tutorialElement);

const setTutorialElement = (element) => {
  tutorialElement.value = element ?? null;
  updateTutorialBounding();
};

/**
 * Re-measures both boxes. Consumers call this after anything that could move either — the
 * same `update` the old single-element version exposed, except the tooltip's own size now
 * feeds positioning too, so measuring only the target would leave clamping working from a
 * stale tooltip size.
 */
const recalculatePosition = () => {
  update();
  updateTutorialBounding();
};

// Gap between the tooltip and the target it's pointing at.
const PLACEMENT_GAP = 8;

// Smallest gap left between the tooltip and the viewport edge when clamping.
const VIEWPORT_MARGIN = 8;

/**
 * How close the arrow may sit to a tooltip corner. Keeps it clear of the rounded corner
 * once clamping has slid the tooltip away from the target's centre.
 */
const ARROW_EDGE_INSET = 20;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Which side of the target the tooltip renders on, chosen automatically from
 * whichever side has the most available space so a target near a viewport edge
 * never gets a tooltip rendered off-screen. Pure
 * function of the target's rect and the viewport size (both already reactive
 * via useElementBounding/useWindowSize), so it re-evaluates on scroll/resize
 * for free and is testable with fake bounding-rect values, no real layout needed.
 */
const placement = computed(() => {
  const spaceAbove = top.value;
  const spaceBelow = viewportHeight.value - bottom.value;
  const spaceLeft = left.value;
  const spaceRight = viewportWidth.value - right.value;

  const verticalSpace = Math.max(spaceAbove, spaceBelow);
  const horizontalSpace = Math.max(spaceLeft, spaceRight);

  if (verticalSpace >= horizontalSpace) {
    return spaceBelow >= spaceAbove ? 'below' : 'above';
  }
  return spaceRight >= spaceLeft ? 'right' : 'left';
});

// The arrow renders on whichever tooltip edge faces the target — the
// opposite side from wherever the tooltip itself was placed.
const liveArrowDirection = computed(
  () =>
    ({
      above: 'bottom',
      below: 'top',
      left: 'right',
      right: 'left',
    })[placement.value],
);

/**
 * The tooltip's top-left corner, in viewport coordinates, before clamping: centred on the
 * target along the cross axis and offset by `PLACEMENT_GAP` along the placement axis.
 *
 * Concrete coordinates rather than CSS transforms (`translate(-50%, -50%)` and friends,
 * which this used to rely on) because a transform can't be clamped — the browser applies it
 * after any positioning, so the box lands wherever it lands, half off-screen if the target
 * is near an edge. Computing the corner ourselves makes the box's extent knowable, and
 * therefore correctable.
 */
const unclampedPosition = computed(() => {
  const targetMidX = left.value + width.value / 2;
  const targetMidY = top.value + height.value / 2;

  switch (placement.value) {
    case 'below':
      return { x: targetMidX - tutorialWidth.value / 2, y: bottom.value + PLACEMENT_GAP };
    case 'left':
      return {
        x: left.value - PLACEMENT_GAP - tutorialWidth.value,
        y: targetMidY - tutorialHeight.value / 2,
      };
    case 'right':
      return { x: right.value + PLACEMENT_GAP, y: targetMidY - tutorialHeight.value / 2 };
    case 'above':
    default:
      return {
        x: targetMidX - tutorialWidth.value / 2,
        y: top.value - PLACEMENT_GAP - tutorialHeight.value,
      };
  }
});

/**
 * The same corner, pulled back inside the viewport. `placement` already picks the side with
 * the most room, but that only decides *which* side — a tooltip taller or wider than the
 * space alongside its target still overhangs, which is what clipped the last step of the
 * Saved Searches walkthrough against the bottom of the window.
 */
const clampedPosition = computed(() => {
  const { x, y } = unclampedPosition.value;

  return {
    x: clamp(x, VIEWPORT_MARGIN, viewportWidth.value - tutorialWidth.value - VIEWPORT_MARGIN),
    y: clamp(y, VIEWPORT_MARGIN, viewportHeight.value - tutorialHeight.value - VIEWPORT_MARGIN),
  };
});

/**
 * Deliberately never hides the tooltip. An earlier version set `visibility: hidden` until the
 * tooltip had been measured, to avoid a one-frame jump — but that makes an unmeasured tooltip
 * permanently invisible, which is indistinguishable from the walkthrough being broken. A
 * tooltip briefly a few pixels off is a far better failure than one nobody can see, so with a
 * zero size the clamp simply keeps it inside the viewport and it settles once measured.
 */
const liveTutorialStyle = computed(() => {
  const { x, y } = clampedPosition.value;

  return {
    position: 'fixed',
    'z-index': 200,
    top: `${y}px`,
    left: `${x}px`,
  };
});

/**
 * Where the arrow sits along the tooltip edge facing the target, as a CSS length from that
 * edge's start. Needed because clamping decouples the tooltip's centre from the target's:
 * the arrow is pinned to the middle of its edge by default, so a clamped tooltip would point
 * at empty space. Kept `ARROW_EDGE_INSET` clear of both corners so it never straddles the
 * rounded edge; when nothing is clamped this lands exactly at the midpoint anyway.
 */
const liveArrowOffset = computed(() => {
  const isVertical = placement.value === 'above' || placement.value === 'below';
  const { x, y } = clampedPosition.value;

  if (isVertical) {
    const targetMidX = left.value + width.value / 2;
    const span = tutorialWidth.value;
    return `${clamp(targetMidX - x, ARROW_EDGE_INSET, span - ARROW_EDGE_INSET)}px`;
  }

  const targetMidY = top.value + height.value / 2;
  const span = tutorialHeight.value;
  return `${clamp(targetMidY - y, ARROW_EDGE_INSET, span - ARROW_EDGE_INSET)}px`;
});

/**
 * While a step transition is in flight (see `isTransitioning`), the step still technically
 * active is often mid-teardown — its target can be sliding off-screen (a closing side sheet's
 * leave transition, still in the DOM for its full duration) or otherwise in a state no longer
 * worth pointing at. Left live, `tutorialStyle`/`arrowDirection`/`arrowOffset` would keep
 * chasing that dying target and clamp the tooltip into a corner as its rect degenerates,
 * only snapping to the correct spot once the transition finishes and a new step goes active.
 * Freezing to the last good values the instant a transition starts avoids that visible jump.
 */
const frozenVisual = ref(null);

const tutorialStyle = computed(() => frozenVisual.value?.style ?? liveTutorialStyle.value);
const arrowDirection = computed(
  () => frozenVisual.value?.arrowDirection ?? liveArrowDirection.value,
);
const arrowOffset = computed(() => frozenVisual.value?.arrowOffset ?? liveArrowOffset.value);

// Tutorial.vue is always told `total-steps="1"`, so its own internal label
// logic can't be used — the Controller owns the real step count and, with
// it, the "Next" vs "Got it!" label a consuming component renders itself
// (e.g. via Tutorial's `tutorial-footer` slot).
const nextButtonLabel = computed(() =>
  activeIndex.value + 1 < totalSteps.value ? 'Next' : 'Got it!',
);

// Whether there's any earlier step to go back to — a consuming component
// uses this to decide whether to render a "Previous" control at all.
const isFirstStep = computed(() => activeIndex.value <= 0);

/** The registry stores component instances as readily as elements; measuring needs the element. */
const resolveStepElement = (index) => {
  const step = steps.value[index];
  if (!step) {
    return null;
  }
  return unrefElement(getWalkthroughTarget(step.stepId));
};

/**
 * Is this element actually visible to the carrier — not merely mounted? Registration only
 * proves a component rendered; it says nothing about a collapsed container, a
 * `display: none` parent, or (the case that prompted this) an element sitting underneath a
 * full-screen overlay. Pointing a tooltip at something invisible is worse than skipping the
 * step, so both are checked:
 *
 * 1. a non-zero bounding rect — catches display:none, zero-size and collapsed containers
 * 2. a hit test at the element's centre — catches anything painted over the top of it
 *
 * An **ancestor** winning the hit test counts as visible, not covered. `elementFromPoint`
 * honours `pointer-events`, so a target that opts out of hit-testing (the Saved Searches
 * button does, once it gains its `active` class) reports its containing wrapper instead —
 * which proves the point lands inside the target's own subtree, i.e. nothing foreign is on
 * top. A genuine overlay, like a modal scrim or a slid-in panel, lives in a different
 * subtree and is neither an ancestor nor a descendant, so it still fails.
 */
const isElementVisible = (element) => {
  if (!element?.getBoundingClientRect) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return false;
  }

  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;

  // Skip the tooltip itself (it can clamp on top of its own target) to find what's really on top.
  const stack = document.elementsFromPoint?.(x, y) ?? [document.elementFromPoint(x, y)];
  const topmost = stack.find((candidate) => !tutorialElement.value?.contains(candidate)) ?? null;

  if (!topmost) {
    // Outside the viewport entirely — the caller decides whether scrolling can help.
    return false;
  }

  return element === topmost || element.contains(topmost) || topmost.contains(element);
};

/**
 * Test, scroll, test — in that order, so an already-visible target is never scrolled to
 * gratuitously. Only when the first test fails is the element brought into view and
 * re-tested; a second failure means it's genuinely unreachable (covered, collapsed) and
 * scrolling can't fix that, so the step is skipped.
 */
const isStepTargetVisible = (index) => {
  const element = resolveStepElement(index);
  if (!element) {
    return false;
  }

  if (isElementVisible(element)) {
    return true;
  }

  element.scrollIntoView?.({ block: 'center' });
  return isElementVisible(element);
};

/**
 * Is the target for this step id visible right now? Same rule the Controller applies when
 * choosing a step, minus the scroll — a caller polling this wants to observe the page
 * settling, not nudge it.
 */
export const isWalkthroughTargetVisible = (stepId) =>
  isElementVisible(unrefElement(getWalkthroughTarget(stepId)));

/**
 * The path we're on, read from the browser rather than `useRoute()`: a step transition runs
 * long after whatever triggered it, and the app is SPA-only, so history keeps
 * `location.pathname` current.
 */
const currentPath = () => (typeof window === 'undefined' ? '' : window.location.pathname);

/**
 * Set right before navigating to a step's `route`; consumed by that destination page's own
 * `scrollToTop` page meta (see `pages/carrier-profile/your-profile.vue`). Nuxt's default
 * scroll-to-top on navigation can land *after* this file's own scroll-into-view work and undo
 * it, so a page with a `scrollIntoView` step opts out of the reset via this flag instead of
 * racing it. Re-exported (unchanged name/shape) from `useWalkthroughLaunch.js` for existing
 * importers.
 */
let skipNextScrollToTopReset = false;

export const consumeSkipScrollToTopReset = () => {
  const shouldSkip = skipNextScrollToTopReset;
  skipNextScrollToTopReset = false;
  return shouldSkip;
};

/**
 * Resolves once the page navigated to has actually mounted.
 *
 * `await navigateTo(...)` only tells you the route was confirmed, not that the destination page has
 * finished setting itself up — and anything a `setup` does in that gap is unsafe. The API client
 * aborts every in-flight request from `router.beforeEach` (see plugins/api.js), so a fetch fired
 * too early is cancelled by the tail of the very navigation that led to it, then swallowed: the
 * saved-search store treats an AbortError as expected and simply leaves its list empty. The
 * symptom is a panel that works when launched from its own page and shows an empty state when
 * launched from anywhere else.
 *
 * `page:finish` is the signal Nuxt gives for that, with a timeout because it fires per real page
 * transition and we would rather start slightly early than not at all.
 */
const PAGE_READY_TIMEOUT_MS = 2000;

const whenPageReady = async () => {
  // Outside a Nuxt runtime (unit tests) there is no page transition to wait for.
  let nuxtApp = null;
  try {
    nuxtApp = useNuxtApp();
  } catch {
    nuxtApp = null;
  }

  if (!nuxtApp?.hook) {
    await nextTick();
    return;
  }

  await new Promise((resolve) => {
    const stopWaiting = () => {
      clearTimeout(timer);
      unhook();
      resolve();
    };
    const unhook = nuxtApp.hook('page:finish', stopWaiting);
    const timer = setTimeout(stopWaiting, PAGE_READY_TIMEOUT_MS);
  });

  await nextTick();
};

/**
 * A `setup` (or a fresh page) can reveal several steps at once — a panel opening one CSS
 * transition, a list rendering several rows once its fetch resolves. `FIRST_VISIBLE_TIMEOUT_MS`
 * is how long to hold out for *any* of them (the mobile case: a setup opens a panel that fetches
 * before rendering, so for a while *no* step is showable — waiting only for "all visible" would
 * time out with nothing on screen and end the walkthrough on the tick it began).
 * `ALL_VISIBLE_TIMEOUT_MS`, once something is showable, is how much longer to wait for the rest —
 * short, since a walkthrough containing a permanently hidden step shouldn't stall on it.
 */
const FIRST_VISIBLE_TIMEOUT_MS = 4000;
const ALL_VISIBLE_TIMEOUT_MS = 1000;
const SETTLE_POLL_MS = 50;

const pollUntil = async (predicate, timeoutMs) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (predicate()) {
      return true;
    }
    await promiseTimeout(SETTLE_POLL_MS);
  }

  return false;
};

const isStepVisible = (step) => isElementVisible(unrefElement(getWalkthroughTarget(step.stepId)));

/**
 * Says out loud, in development, that some of a just-prepared group of steps can't be shown.
 *
 * Every authoring mistake in this feature fails the same silent way: the walkthrough runs, drops
 * the step, and reports nothing — a step id no component registers, a `ref` bound with `:ref`
 * so it never populates, a control that has a separate mobile implementation nobody registered,
 * a target still hidden behind a panel because the step declares no `setup`. Each of those cost
 * real debugging time to find, and each is obvious the moment it's named.
 *
 * Dev-only: in production a skipped step is deliberate, graceful behavior, not something to
 * narrate to a carrier's console.
 */
const warnAboutSkippedGroup = (group) => {
  if (!useIsDevelopment()) {
    return;
  }

  const skipped = group.filter((step) => !isStepVisible(step));
  if (!skipped.length) {
    return;
  }

  const details = skipped.map((step) => {
    const registered = Boolean(unrefElement(getWalkthroughTarget(step.stepId)));
    return registered
      ? `  • ${step.stepId} — registered, but not visible. Is it behind a panel this walkthrough does not open with a "setup"?`
      : `  • ${step.stepId} — no component registered this id. Check useWalkthroughTarget + a static ref="…" binding (never :ref), in every component that renders this control, mobile and desktop.`;
  });

  // eslint-disable-next-line no-console -- authoring aid; dev-only, see above
  console.warn(
    [
      `[walkthrough] "${group[0].stepId}" is starting with ${skipped.length} of ${group.length} step(s) that cannot be shown:`,
      ...details,
    ].join('\n'),
  );
};

/**
 * Whether showing this step requires anything beyond the synchronous visibility check below —
 * a page it isn't already on, or a `setup` to run. Steps with neither (every Feature
 * Announcement step today, and most onboarding steps once past the one that opened a panel)
 * skip this entirely, keeping `settleActiveIndex`/`advance`/`back` fully synchronous for them —
 * see the file-level comment.
 */
const stepNeedsPrep = (step) =>
  Boolean(step.setup) || Boolean(step.route && step.route !== currentPath());

/**
 * The run of steps, starting at `index`, that this preparation is responsible for settling —
 * `index` itself, plus every following step that needs no prep of its own (so shares whatever
 * page/setup `index` just put in place). A single-page announcement's whole step array is one
 * such group, since none of its later steps declare their own `route`/`setup` — reproducing the
 * old "wait for every step, not just the first" behavior exactly. A multi-page onboarding
 * walkthrough instead gets one group per page/setup boundary.
 */
const collectPrepGroup = (index) => {
  const group = [steps.value[index]];
  let i = index + 1;
  while (i < steps.value.length && !stepNeedsPrep(steps.value[i])) {
    group.push(steps.value[i]);
    i += 1;
  }
  return group;
};

/**
 * Gets a step's target — and its whole prep group's targets — into a showable state: navigates
 * to its `route` if it declares one and we're not already there, runs its `setup` if it declares
 * one, then gives the DOM the two-phase wait described above before the caller's own visibility
 * check runs. Does not itself scroll or retry — `isStepTargetVisible` right after this already
 * does that, and duplicating it here would just do it twice.
 */
const prepareStep = async (step, group) => {
  if (step.route && step.route !== currentPath()) {
    if (step.scrollIntoView) {
      skipNextScrollToTopReset = true;
    }
    await navigateTo(step.route);
    await whenPageReady();
  }

  if (step.setup) {
    runWalkthroughSetup(step.setup);
  }

  await pollUntil(() => group.some(isStepVisible), FIRST_VISIBLE_TIMEOUT_MS);
  await pollUntil(() => group.every(isStepVisible), ALL_VISIBLE_TIMEOUT_MS);
  warnAboutSkippedGroup(group);
};

const withTransition = async (work) => {
  isTransitioning.value = true;
  frozenVisual.value = {
    style: liveTutorialStyle.value,
    arrowDirection: liveArrowDirection.value,
    arrowOffset: liveArrowOffset.value,
  };
  try {
    await work();
  } finally {
    isTransitioning.value = false;
    frozenVisual.value = null;
  }
};

// Set by start() for the run currently in progress; fired at most once each,
// then cleared, so a run's callbacks can never fire twice or leak into the
// next run. Story 8 (analytics) hangs its trackEvent() calls off these —
// this composable stays ignorant of announcement_id/trackEvent itself,
// since the caller of start() already has that context.
let onCompleteCallback = null;
let onCancelCallback = null;

const clearLifecycleCallbacks = () => {
  onCompleteCallback = null;
  onCancelCallback = null;
};

// The single place that both advances to a step and skips it if its target
// isn't currently visible, preparing each candidate (see stepNeedsPrep/
// prepareStep above) before checking. Running out of steps this way ends the
// walkthrough and counts as "completed" — whether that's because advance()
// reached the true last step, or because every remaining step's target
// disappeared — there's no separate "stuck" event, per the Design Doc's
// Analytics table. Only cancel() below is a distinct, user-initiated exit.
const settleActiveIndex = async (index) => {
  const wasRunning = isRunning.value;
  let nextIndex = index;

  while (nextIndex < steps.value.length) {
    const step = steps.value[nextIndex];

    if (stepNeedsPrep(step)) {
      const group = collectPrepGroup(nextIndex);
      await withTransition(() => prepareStep(step, group));
    }

    if (isStepTargetVisible(nextIndex)) {
      activeIndex.value = nextIndex;
      // Started fresh (rather than advancing/backing an already-running walkthrough) — done
      // here, synchronously, rather than in start()'s own post-await continuation, which on
      // the fast path (no step needing prep) would otherwise defer this a needless microtask:
      // `await` always yields at least once, even when the awaited call never itself suspended.
      if (!wasRunning) {
        startVisibilityWatch();
      }
      return;
    }

    nextIndex += 1;
  }

  const finalStepIndex = activeIndex.value;
  const finishedTotalSteps = totalSteps.value;
  activeIndex.value = -1;
  stopVisibilityWatch();

  if (wasRunning) {
    onCompleteCallback?.({ finalStepIndex, totalSteps: finishedTotalSteps });
  }
  clearLifecycleCallbacks();
};

const start = async (walkthroughSteps, { onComplete, onCancel } = {}) => {
  steps.value = walkthroughSteps;
  onCompleteCallback = onComplete ?? null;
  onCancelCallback = onCancel ?? null;
  await settleActiveIndex(0);
};

const advance = async () => {
  if (!isRunning.value) return;
  await settleActiveIndex(activeIndex.value + 1);
};

/**
 * Undoes whatever the step being left set up, so going back doesn't leave the UI in a state
 * that only makes sense moving forward. If that step's setup declared a `teardown`, it runs —
 * it alone knows how to reverse its own effect. If it didn't, there's no defined way to reverse
 * it, so this replays forward from the very start up to (and including) the target step instead
 * of guessing: the same per-step prep a normal forward run already does, just capped early. See
 * ADR 0006 — this can lose whatever the carrier typed into a step that gets replayed past, an
 * accepted trade-off for setups nobody has written a teardown for yet.
 */
const undoStepForBack = async (leavingStep, targetIndex) => {
  const didTeardown = leavingStep.setup ? runWalkthroughTeardown(leavingStep.setup) : false;
  if (didTeardown) {
    // Mirrors prepareStep's own wait: a teardown's state change (e.g. a Pinia ref flip) doesn't
    // synchronously re-render the DOM, so checking visibility the instant this returns can see
    // the target as not-yet-mounted — misread by back()'s loop as "this teardown didn't work,"
    // cascading into undoing an extra step it never needed to touch.
    await pollUntil(() => isStepVisible(steps.value[targetIndex]), FIRST_VISIBLE_TIMEOUT_MS);
    return;
  }

  for (let i = 0; i <= targetIndex; i += 1) {
    const step = steps.value[i];
    if (stepNeedsPrep(step)) {
      await prepareStep(step, [step]);
    }
  }
};

const back = async () => {
  if (!isRunning.value) return;

  let leavingIndex = activeIndex.value;
  let candidateIndex = activeIndex.value - 1;

  while (candidateIndex >= 0) {
    const leavingStep = steps.value[leavingIndex];

    if (leavingStep.setup) {
      await withTransition(() => undoStepForBack(leavingStep, candidateIndex));
    }

    if (isStepTargetVisible(candidateIndex)) {
      activeIndex.value = candidateIndex;
      return;
    }

    leavingIndex = candidateIndex;
    candidateIndex -= 1;
  }
};

// Distinct from settleActiveIndex's skip-to-end: a user-initiated exit,
// never conflated with a step being auto-skipped or the walkthrough running
// its natural course to the end.
const cancel = () => {
  if (!isRunning.value) return;

  const finalStepIndex = activeIndex.value;
  const finishedTotalSteps = totalSteps.value;
  activeIndex.value = -1;
  stopVisibilityWatch();

  onCancelCallback?.({ finalStepIndex, totalSteps: finishedTotalSteps });
  clearLifecycleCallbacks();
};

/**
 * While a walkthrough runs, keep checking that the step being shown is still visible, and
 * settle off it the moment it isn't.
 *
 * The unmount watcher below can't cover this: the containers a walkthrough points into
 * usually stay mounted and merely hide. Close the Saved Searches panel mid-walkthrough and
 * its buttons are still registered, still measurable — just slid off-screen — so without
 * this the tooltip would hang around pointing at a panel that isn't there any more.
 *
 * Polled rather than watched because visibility isn't reactive: neither a CSS transform nor
 * something painting over the target notifies anyone. The interval only runs for the
 * lifetime of a walkthrough, which is seconds, and deliberately uses the plain visibility
 * test — a poll must not scroll the page underneath the carrier.
 */
const VISIBILITY_WATCH_MS = 300;
let visibilityWatchTimer = null;

const stopVisibilityWatch = () => {
  if (visibilityWatchTimer) {
    clearInterval(visibilityWatchTimer);
    visibilityWatchTimer = null;
  }
};

const startVisibilityWatch = () => {
  stopVisibilityWatch();

  if (typeof window === 'undefined') {
    return;
  }

  visibilityWatchTimer = setInterval(() => {
    if (!isRunning.value) {
      stopVisibilityWatch();
      return;
    }

    // A settleActiveIndex() call already in flight (navigating, running a setup) hasn't
    // bumped activeIndex yet, so its own target can look exactly like "vanished" for the
    // whole window it's busy — most concretely, a setup that mutates the DOM (opening a form)
    // can hide the *previous* step's target as a side effect. Without this guard, that reads
    // as this step needing to settle too, firing a second, overlapping settleActiveIndex() that
    // re-runs the very setup already in progress.
    if (isTransitioning.value) {
      return;
    }

    if (!isElementVisible(resolveStepElement(activeIndex.value))) {
      // Same treatment as a target that unmounted: move to the next visible step, or end.
      settleActiveIndex(activeIndex.value + 1);
    }
  }, VISIBILITY_WATCH_MS);
};

/**
 * The active step's target can disappear from under a running walkthrough — the carrier
 * navigates away, or closes the panel a `setup` opened. Without this the tooltip kept
 * rendering at the last measured position, pointing at nothing, with no way out.
 *
 * Settling forward from the current index re-runs the ordinary skip logic: the next
 * visible step becomes active, or the walkthrough ends if none remain. That doubles as the
 * carrier's escape hatch, since navigating away unmounts every target on the page.
 *
 * `flush: 'sync'` is required, not stylistic. This watcher is created at module scope, with
 * no owning component instance, and Vue's pre/post queues for an instance-less watcher are
 * never flushed — verified both ways: with the default `pre` (and with `post`) the callback
 * simply never runs, however many ticks you wait.
 *
 * Guarded on `isTransitioning` for the same reason `startVisibilityWatch`'s interval is: a
 * settleActiveIndex() call already in flight hasn't bumped activeIndex yet, so its own
 * (previous) target can vanish as a side effect of the very setup that call is running —
 * `flush: 'sync'` means this fires immediately, mid-await, which would otherwise start a
 * second, overlapping settleActiveIndex() and re-run that setup.
 */
watch(
  activeTargetElement,
  (element) => {
    if (isRunning.value && !element && !isTransitioning.value) {
      settleActiveIndex(activeIndex.value + 1);
    }
  },
  { flush: 'sync' },
);

export const useWalkthroughController = () => ({
  activeStep,
  activeIndex,
  totalSteps,
  isRunning,
  isTransitioning,
  tutorialStyle,
  arrowDirection,
  arrowOffset,
  setTutorialElement,
  nextButtonLabel,
  isFirstStep,
  start,
  advance,
  back,
  cancel,
  recalculatePosition,
});
